import { autoinject, TaskQueue, bindable } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ExpandEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { localSort } from 'app/shared/lib/sorting';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { LevelOfCareRevenueCodeMappingReloadEvent } from 'app/um/lib/events';
import { SaveRevenueCodeMapping } from 'app/um/components/levels-of-care/save-revenue-code-mapping';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import * as UM from 'app/um/models/essette-api';

const expandedProviderLocationsCache = new Map<string, Array<UM.ProviderLocation>>();

@autoinject()
export class RevenueCodeMappings extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey: string = "um-manage-level-of-care-revenue-code-mappings";

    @bindable
    protected rememberExpand: boolean = true;

    // @bindable
    // protected selectedLevelOfCare: UM.LevelOfCare | null = null;

    private levelOfCareRevenueCodeMappingReload: Subscription | null = null;

    protected providerLocations: ReadonlyArray<UM.ProviderLocation> | null = null;

    private sorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<UM.ProviderLocation[]>;
    protected $pager!: EssPagerCustomElement;

    protected $saveRevenueCodeMappingModal!: IPopup;
    protected $form!: IForm;
    protected $revenueCodeMappingForm!: Element;
    protected $saveRevenueCodeMapping!: SaveRevenueCodeMapping;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    protected attached(): void {
        this.levelOfCareRevenueCodeMappingReload = this.events.subscribe(LevelOfCareRevenueCodeMappingReloadEvent, (e: LevelOfCareRevenueCodeMappingReloadEvent) => {
            this.reload();
        });
    }

    protected detached(): void {
        if(this.levelOfCareRevenueCodeMappingReload) {
            this.levelOfCareRevenueCodeMappingReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    /** Handles the grid's sort event for the detail tables */
    protected applyLocalSort(event: SortChangeEvent, levelsOfCare: (UM.LevelOfCare)[]): void {
        const { property, direction } = event.detail;
        const { localSorting } = this;
        if(direction === null) {
            localSorting.remove(property);
        } else {
            localSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(levelsOfCare, localSorting);
        if(this.localSorting.entries.length == 0) {
            this.reload();
        };
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadProviderLocation();
    }

    public formSuccess(event?: any) {
        this.saveRevenueCodeMappingClose();
        this.reload();
    }

    // #region Add Revenue Code Mapping Modal Functions
    protected saveRevenueCodeMapping(levelOfCare?: any, providerLocation?: any): void {
        const model = this.$saveRevenueCodeMapping;

        model.isEdit = levelOfCare && providerLocation;
        if(model.isEdit) {
            model.revenueCode = levelOfCare.detail.revenueCode;
            model.selectedLevelOfCare = levelOfCare;
            model.selectedProvider = providerLocation;
        } else {
            model.reset();
        }

        model.reload();

        this.$saveRevenueCodeMappingModal.open();
    }

    protected saveRevenueCodeMappingClose(): void {
        this.$saveRevenueCodeMappingModal.close();
    }

    // #endregion Add Revenue Code Mapping Modal Functions

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the Provider Locations into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedProviderLocations = expandedProviderLocationsCache.get(this.expandedCacheKey);
            if(expandedProviderLocations !== undefined) {
                this.$table.restoreExpanded(expandedProviderLocations);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<UM.ProviderLocation>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedProviderLocationsCache.set(this.expandedCacheKey, detail.items);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadProviderLocation(): Promise<void> {

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.service.loadProviderLocation(options, includes, true);
            this.providerLocations = response.results;
            this.resultCount = response.count;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }


    @apiCall
    private async deleteLevelsOfCareDetail(levelOfCare: UM.LevelOfCare, selectedProviderLocation: UM.ProviderLocation): Promise<void> {

        const deleteDetails: UM.LevelOfCare = {
            id: levelOfCare.id,
            providerLocations: [{ id: selectedProviderLocation.id }]
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.deleteLevelsOfCareDetail(deleteDetails);
            isSuccessful = response.isSuccessful;
            Promise.all([this.reload()]);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('um:auth.levelOfCare.revenueCode.$', isSuccessful);
            this.state.endSave();
        }
    }

}
